/**
 * The two controls a screen ends on: the way back, and the way on.
 *
 * Three screens in a row ask the same thing of somebody — read this, then
 * either go on or go back — and all three answered it differently. The game's
 * page put Back in the header, its action at the top of the content and a
 * Store pill in the corner; the arrival screen put Back in the header and its
 * action at the foot of a form that grows when you open the avatar grid; the
 * lobby had no way back at all and printed its Start button twice, once per
 * tab.
 *
 * So the same pair, in the same place, on all three. Pinned to the bottom of
 * the phone, because that is where a thumb rests and because a control that
 * moves as the page grows is one you have to look for each time. Going back is
 * on the left where a back gesture starts; going on is on the right and wider,
 * which is the shape of the choice rather than a decoration.
 *
 * ## Why it is `sticky` and not `fixed`
 *
 * A fixed bar is out of the flow, so every page carrying one needs bottom
 * padding the height of the bar or its last line hides underneath — a number
 * written in two places that drift the moment the bar gains a second line.
 * Sticky keeps the bar in the flow and pins it anyway: `margin-top: auto`
 * drops it to the bottom of a short page, and `bottom: 0` holds it there
 * through a long one. Nothing has to know how tall it is.
 */

import { Link } from 'react-router-dom';
import { Icon } from '../render/Icon.js';

export function ActionBar({ children }: { children: React.ReactNode }) {
  return <div className="action-bar">{children}</div>;
}

/**
 * The way back, shaped like the action beside it.
 *
 * `.cta` rather than the header's small `.page-back` text link: side by side
 * with a gold button, a 0.74rem line of text does not read as the other half
 * of a pair. Same box, same height, quieter fill.
 *
 * The chevron is the one we own, mirrored — a second file for a left-pointing
 * copy of the same drawing would be a second thing to keep in step with it.
 */
export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link className="cta action-back" to={to}>
      <Icon name="chevron-right" className="cta-mark" />
      <span className="cta-label">{label}</span>
    </Link>
  );
}

/** The same button where there is no route to go to, only something to leave. */
export function BackButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="cta action-back" onClick={onClick}>
      <Icon name="chevron-right" className="cta-mark" />
      <span className="cta-label">{label}</span>
    </button>
  );
}
